import type { FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Globe, Mail, MapPin, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { Logo } from '@/components/generals/logo'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'

import {
    contact,
    defaultWhatsappMessage,
    navLinks,
    whatsappLink
} from '@/constants/site'

interface MenuIconProps {
    open: boolean
}

const MenuIcon: FC<MenuIconProps> = ({ open }): ReactNode => {
    return (
        <span aria-hidden='true' className='relative block h-3.5 w-6'>
            <span
                className={cn(
                    'absolute top-0 left-0 h-[2px] w-6 rounded-full bg-current transition-all duration-300 ease-out',
                    open && 'top-1/2 -translate-y-1/2 rotate-45'
                )}
            />
            <span
                className={cn(
                    'absolute top-1/2 left-0 h-[2px] w-4 -translate-y-1/2 rounded-full bg-current transition-all duration-300 ease-out',
                    open && 'translate-x-3 opacity-0'
                )}
            />
            <span
                className={cn(
                    'absolute bottom-0 left-0 h-[2px] w-6 rounded-full bg-current transition-all duration-300 ease-out',
                    open && 'bottom-1/2 translate-y-1/2 -rotate-45'
                )}
            />
        </span>
    )
}

const TopBar: FC = (): ReactNode => {
    return (
        <div className='hidden bg-brand-ink text-[12.5px] text-brand-mist md:block'>
            <div className='mx-auto flex h-10 max-w-7xl items-center justify-between gap-6 px-6 lg:px-10'>
                <p className='inline-flex items-center gap-2'>
                    <MapPin className='size-3.5 text-brand-gold' aria-hidden='true' />
                    Based in Akure, Nigeria. Serving clients across Nigeria and worldwide.
                </p>

                <div className='flex items-center gap-6'>
                    <a href={contact.phoneHref} className='inline-flex items-center gap-2 transition-colors hover:text-brand-white'>
                        <Phone className='size-3.5 text-brand-gold' aria-hidden='true' />
                        {contact.phoneDisplay}
                    </a>

                    <a href={`mailto:${contact.email}`} className='hidden items-center gap-2 transition-colors hover:text-brand-white lg:inline-flex'>
                        <Mail className='size-3.5 text-brand-gold' aria-hidden='true' />
                        {contact.email}
                    </a>

                    <a
                        href={whatsappLink(defaultWhatsappMessage)}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-2 transition-colors hover:text-brand-white'
                    >
                        <WhatsappIcon className='size-3.5 text-brand-gold' />
                        Chat on WhatsApp
                    </a>
                </div>
            </div>
        </div>
    )
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const dialogRef = useRef<HTMLDialogElement>(null)
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [iconOpen, setIconOpen] = useState(false)
    const [activeSection, setActiveSection] = useState('home')
    const isHome = router.pathname === '/'

    useEffect(() => {
        const handleScroll = (): void => setScrolled(window.scrollY > 16)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!isHome) return

        const sections = navLinks
            .map(link => document.getElementById(link.section))
            .filter((section): section is HTMLElement => Boolean(section))

        if (!sections.length) return

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActiveSection(entry.target.id)
            })
        }, { rootMargin: '-40% 0px -55% 0px' })

        sections.forEach(section => observer.observe(section))

        return () => observer.disconnect()
    }, [isHome])

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return
        if (menuOpen && !dialog.open) dialog.showModal()
        if (!menuOpen && dialog.open) dialog.close()

        if (!menuOpen) {
            setIconOpen(false)

            return
        }

        const timer = window.setTimeout(() => setIconOpen(true), 40)

        return () => window.clearTimeout(timer)
    }, [menuOpen])

    useEffect(() => {
        const handleRouteStart = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', handleRouteStart)
        router.events.on('hashChangeStart', handleRouteStart)

        return () => {
            router.events.off('routeChangeStart', handleRouteStart)
            router.events.off('hashChangeStart', handleRouteStart)
        }
    }, [router.events])

    const closeMenu = (): void => {
        dialogRef.current?.close()
        setMenuOpen(false)
    }

    const handleHomeClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        closeMenu()

        if (!isHome) return

        event.preventDefault()
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const pageSection = router.pathname.startsWith('/services') ? 'services' : router.pathname === '/about' ? 'about' : null
    const currentSection = isHome ? activeSection : pageSection

    return (
        <>
            <TopBar />

            <header
                className={cn(
                    'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
                    scrolled
                        ? 'border-brand-sand bg-brand-white/90 shadow-[0_12px_40px_-24px_rgba(32,32,32,0.35)] backdrop-blur-xl'
                        : 'border-transparent bg-brand-white'
                )}
            >
                <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-6 lg:px-10'>
                    <Link
                        href='/'
                        onClick={handleHomeClick}
                        aria-label='Loveth Tasty Global, back to the home page'
                        className='shrink-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-4'
                    >
                        <Logo />
                    </Link>

                    <nav aria-label='Main' className='hidden xl:block'>
                        <ul className='flex items-center gap-7'>
                            {navLinks.map(link => {
                                const isActive = currentSection === link.section

                                return (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            onClick={link.section === 'home' ? handleHomeClick : undefined}
                                            aria-current={isActive ? 'location' : undefined}
                                            className={cn(
                                                'relative py-2 text-[13.5px] font-medium transition-colors after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:origin-left after:rounded-full after:bg-brand-orange after:transition-transform after:duration-300 hover:text-brand-charcoal hover:after:scale-x-100 focus-visible:outline-none focus-visible:after:scale-x-100',
                                                isActive ? 'text-brand-charcoal after:scale-x-100' : 'text-brand-charcoal/70 after:scale-x-0'
                                            )}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2.5'>
                        <Link href='/book' className={buttonStyles({ variant: 'primary', size: 'sm', className: 'hidden sm:inline-flex' })}>
                            Book Catering
                        </Link>

                        <Link href='/order-from-abroad' className={buttonStyles({ variant: 'dark', size: 'sm', className: 'hidden md:inline-flex' })}>
                            <Globe className='size-4' aria-hidden='true' />
                            Order From Abroad
                        </Link>

                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-label='Open menu'
                            aria-haspopup='dialog'
                            aria-expanded={menuOpen}
                            className='inline-flex size-11 items-center justify-center rounded-full border border-brand-sand text-brand-charcoal transition-colors hover:bg-brand-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange xl:hidden'
                        >
                            <MenuIcon open={false} />
                        </button>
                    </div>
                </div>
            </header>

            <dialog
                ref={dialogRef}
                aria-label='Site menu'
                onClose={() => setMenuOpen(false)}
                className='m-0 h-dvh max-h-none w-full max-w-none border-0 bg-brand-white p-0 text-brand-charcoal backdrop:bg-transparent open:animate-in open:fade-in open:duration-300'
            >
                <div className='flex h-full flex-col'>
                    <div className='flex h-20 shrink-0 items-center justify-between px-5 sm:px-6'>
                        <Link href='/' onClick={handleHomeClick} aria-label='Loveth Tasty Global, back to the home page'>
                            <Logo />
                        </Link>

                        <button
                            type='button'
                            onClick={closeMenu}
                            aria-label='Close menu'
                            className='inline-flex size-11 items-center justify-center rounded-full border border-brand-sand text-brand-charcoal transition-colors hover:bg-brand-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange'
                        >
                            <MenuIcon open={iconOpen} />
                        </button>
                    </div>

                    <nav aria-label='Mobile' className='flex-1 overflow-y-auto px-5 pt-4 pb-6 sm:px-6'>
                        <ul>
                            {navLinks.map((link, index) => {
                                const isActive = currentSection === link.section

                                return (
                                    <li
                                        key={link.href}
                                        style={{ animationDelay: `${90 + index * 45}ms` }}
                                        className={cn(
                                            'border-b border-brand-sand fill-mode-both',
                                            menuOpen && 'animate-in fade-in slide-in-from-bottom-3 duration-500'
                                        )}
                                    >
                                        <Link
                                            href={link.href}
                                            onClick={link.section === 'home' ? handleHomeClick : closeMenu}
                                            aria-current={isActive ? 'location' : undefined}
                                            className='group flex items-center justify-between py-3.5 font-heading text-[2rem] leading-tight font-semibold transition-colors hover:text-brand-burnt'
                                        >
                                            <span className={cn(isActive && 'text-brand-burnt')}>
                                                {link.label}
                                            </span>

                                            <ArrowUpRight className='size-5 text-brand-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5' aria-hidden='true' />
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='shrink-0 space-y-4 border-t border-brand-sand bg-brand-cream px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6'>
                        <div className='grid gap-3 sm:grid-cols-2'>
                            <Link href='/book' onClick={closeMenu} className={buttonStyles({ variant: 'primary', className: 'w-full' })}>
                                Book Catering
                            </Link>

                            <Link href='/order-from-abroad' onClick={closeMenu} className={buttonStyles({ variant: 'dark', className: 'w-full' })}>
                                <Globe className='size-4' aria-hidden='true' />
                                Order From Abroad
                            </Link>
                        </div>

                        <div className='flex flex-wrap items-center justify-between gap-3 text-sm text-brand-stone'>
                            <a href={contact.phoneHref} className='inline-flex items-center gap-2 font-medium text-brand-charcoal'>
                                <Phone className='size-4 text-brand-burnt' aria-hidden='true' />
                                {contact.phoneDisplay}
                            </a>

                            <a
                                href={whatsappLink(defaultWhatsappMessage)}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-2 font-medium text-brand-charcoal'
                            >
                                <WhatsappIcon className='size-4 text-brand-burnt' />
                                WhatsApp us
                            </a>
                        </div>
                    </div>
                </div>
            </dialog>
        </>
    )
}
